import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useActualInventoryContext } from "Hooks/useActualInventoryContext";
import useYupValidation from "Hooks/useYupValidation";
import {
  ItemMEAttribute,
  ActualInventoryChecklistTabFields,
  ItemJobStatus,
  QuestionBooleanType,
} from "Util/enums";
import RadioGroup from "Components/Form/RadioGroup";
import { Wrapper, Title, ButtonSection, Option, Group, GroupLabel, GroupWrapper } from "./styled";

export default function MEChecklist() {
  const { t: translate } = useTranslation();

  const { itemJob, setItemJob, errors } = useActualInventoryContext();

  const { fieldHasError, getErrorByField } = useYupValidation(errors || []);

  const [option, setOption] = useState<string>(ActualInventoryChecklistTabFields.ORIGIN);

  const isOptionOrigin = useMemo(() => {
    return option === ActualInventoryChecklistTabFields.ORIGIN;
  }, [option]);

  const getValue = useMemo(() => {
    if (option === ActualInventoryChecklistTabFields.ORIGIN)
      return { done: itemJob?.meDone, working: itemJob?.meWorking };

    return { done: itemJob?.meDoneDelivery, working: itemJob?.meWorkingDelivery };
  }, [itemJob, option]);

  const setValue = (value: string, attribute: string) => {
    setItemJob((state) => ({ ...(state || {}), [attribute]: value } as any));
  };

  const getAttributes = (field: string) => {
    if (field === ActualInventoryChecklistTabFields.DONE_FIELD) {
      if (option === ActualInventoryChecklistTabFields.ORIGIN) return ItemMEAttribute.DONE;

      return ItemMEAttribute.DONE_DELIVERY;
    }

    if (field === ActualInventoryChecklistTabFields.WORKING_FIELD) {
      if (option === ActualInventoryChecklistTabFields.ORIGIN) return ItemMEAttribute.WORKING;

      return ItemMEAttribute.WORKING_DELIVERY;
    }

    return "";
  };

  return (
    <Wrapper tabs={!!itemJob?.id}>
      <Title>{translate("meChecklist")}</Title>
      <ButtonSection>
        <Option
          position
          active={option === ActualInventoryChecklistTabFields.ORIGIN}
          onClick={() => setOption(ActualInventoryChecklistTabFields.ORIGIN)}
        >
          {translate("origin")}
        </Option>
        <Option
          disabled={itemJob?.status !== ItemJobStatus.UPLIFTED}
          active={option === ActualInventoryChecklistTabFields.DESTINATION}
          onClick={() =>
            itemJob?.status !== ItemJobStatus.UPLIFTED
              ? undefined
              : setOption(ActualInventoryChecklistTabFields.DESTINATION)
          }
        >
          {translate("destination")}
        </Option>
      </ButtonSection>
      <GroupWrapper>
        <Group>
          <GroupLabel>{translate("hasTheMEChecklistBeenDoneOnThisItem")}*</GroupLabel>
          <RadioGroup
            aria-label="medone"
            value={getValue.done}
            error={fieldHasError(
              `meOriginChecklist.${isOptionOrigin ? "meDone" : "meDoneDelivery"}`
            )}
            errorMessage={getErrorByField(
              `meOriginChecklist.${isOptionOrigin ? "meDone" : "meDoneDelivery"}`
            )}
            onChange={(event) =>
              setValue(
                event.target.value,
                getAttributes(ActualInventoryChecklistTabFields.DONE_FIELD)
              )
            }
            options={[
              {
                label: translate("yes"),
                value: QuestionBooleanType.YES,
              },
              {
                label: translate("no"),
                value: QuestionBooleanType.NO,
              },
              {
                label: translate("notAuthorizedByCustomer"),
                value: "notauth",
              },
            ]}
          />
        </Group>

        {getValue.done === QuestionBooleanType.YES && (
          <Group>
            <GroupLabel>{translate("isItWorking")}</GroupLabel>
            <RadioGroup
              value={getValue.working}
              error={fieldHasError(
                `meOriginChecklist.${isOptionOrigin ? "meWorking" : "meWorkingDelivery"}`
              )}
              errorMessage={getErrorByField(
                `meOriginChecklist.${isOptionOrigin ? "meWorking" : "meWorkingDelivery"}`
              )}
              onChange={(event) =>
                setValue(
                  event.target.value,
                  getAttributes(ActualInventoryChecklistTabFields.WORKING_FIELD)
                )
              }
              options={[
                {
                  label: translate("yes"),
                  value: QuestionBooleanType.YES,
                },
                {
                  label: translate("no"),
                  value: QuestionBooleanType.NO,
                },
              ]}
            />
          </Group>
        )}
      </GroupWrapper>
    </Wrapper>
  );
}
